import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';

export type OfflinePaymentMethodType = 'venmo' | 'cashapp' | 'paypal' | 'zelle' | 'check' | 'cash' | 'custom';

export interface ManualPayment {
  id: string;
  orgId: string;
  quoteId: string;
  method: OfflinePaymentMethodType;
  amountCents: number;
  currency: string;
  installmentId?: string;
  referenceNumber?: string;
  note?: string;
  checkPhotoUrl?: string;
  loggedBy: string;
  loggedByName: string;
  createdAt: string;
}

interface ListResponse {
  success: boolean;
  data: ManualPayment[];
}

interface LogResponse {
  success: boolean;
  data: ManualPayment;
}

export function useManualPayments(orgId: string | null, quoteId: string | null) {
  const [payments, setPayments] = useState<ManualPayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [logging, setLogging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPayments = useCallback(async () => {
    if (!orgId || !quoteId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<ListResponse>(`/jobs/${orgId}/${quoteId}/manual-payments`);
      setPayments(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, [orgId, quoteId]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const logPayment = useCallback(
    async (data: {
      method: OfflinePaymentMethodType;
      amountCents: number;
      installmentId?: string;
      referenceNumber?: string;
      note?: string;
      checkPhoto?: { imageData: string; fileName: string };
    }) => {
      if (!orgId || !quoteId) throw new Error('No job loaded');
      setLogging(true);
      setError(null);
      try {
        const res = await api.post<LogResponse>(
          `/jobs/${orgId}/${quoteId}/manual-payments`,
          data,
        );
        setPayments(prev => [res.data, ...prev]);
        return res.data;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to log payment';
        setError(message);
        throw err;
      } finally {
        setLogging(false);
      }
    },
    [orgId, quoteId],
  );

  const voidPayment = useCallback(
    async (paymentId: string) => {
      if (!orgId || !quoteId) throw new Error('No job loaded');
      setError(null);
      try {
        await api.del(`/jobs/${orgId}/${quoteId}/manual-payments/${paymentId}`);
        setPayments(prev => prev.filter(p => p.id !== paymentId));
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to void payment';
        setError(message);
        throw err;
      }
    },
    [orgId, quoteId],
  );

  return { payments, loading, logging, error, logPayment, voidPayment, refetch: fetchPayments };
}
